import { useAuth } from '../../context/AuthContext'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { useBugButtonVisibility } from '../../hooks/useBugButtonVisibility'
import { Link } from 'react-router-dom'

function ProfileActionRow({
  to,
  label,
  detail,
  badge,
}: {
  to: string
  label: string
  detail?: string
  badge?: string
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left hover:bg-slate-50"
      >
        <div className="min-w-0">
          <p className="text-[12px] font-bold text-slate-900">{label}</p>
          {detail ? <p className="mt-0.5 text-[10px] text-slate-500">{detail}</p> : null}
        </div>
        {badge ? <span className="fs-tag fs-tag-done shrink-0">{badge}</span> : null}
      </Link>
    </li>
  )
}

function ProfileDisabledRow({
  label,
  detail,
}: {
  label: string
  detail: string
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-3.5 py-2.5 text-slate-400 opacity-70">
      <div className="min-w-0">
        <p className="text-[12px] font-bold">{label}</p>
        <p className="mt-0.5 text-[10px]">{detail}</p>
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wide">Soon</span>
    </li>
  )
}

export function FieldSurveyorProfilePage() {
  const { session, signOut, viewerContext } = useAuth()
  const email = session?.user?.email ?? ''
  const initials = email.split('@')[0]?.slice(0, 2).toUpperCase() ?? '??'
  const displayName = session?.user?.user_metadata?.full_name ?? email.split('@')[0] ?? ''
  const [, setBugVisible] = useBugButtonVisibility()

  return (
    <FieldAppShell eyebrow="Field surveyor" title="Profile" backTo="/today">
      <div className="space-y-3">
        {/* Identity card */}
        <div className="fs-surface p-3.5">
          <div className="flex items-center gap-3">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-brand text-[15px] font-bold"
              style={{ background: 'linear-gradient(145deg, var(--brand-accent), #c98e0e)', color: '#1c1303' }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-brand text-[14px] font-bold text-[var(--brand-deep)] truncate">{displayName}</p>
              <p className="text-[11px] text-slate-500 truncate">{email}</p>
            </div>
          </div>
        </div>

        {/* Tenant card */}
        <div className="fs-surface p-3.5">
          <p className="fs-label mb-1.5">Tenant</p>
          <p className="font-brand text-[13px] font-bold text-[var(--brand-deep)]">
            Your organization
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {viewerContext?.tier === 'field_surveyor_solo'
              ? 'Solo surveyor · You own this tenant'
              : `${viewerContext?.tier ?? 'Surveyor'} tier`}
          </p>
        </div>

        {/* Nav list */}
        <div className="fs-surface overflow-hidden">
          <ul className="divide-y" style={{ borderColor: 'rgba(23,86,141,0.16)' }}>
            <li className="flex items-center gap-2.5 px-3.5 py-2.5 text-[12px] font-bold text-[var(--brand-primary)]" style={{ background: 'var(--brand-light)' }}>
              Account &amp; profile
            </li>
            <ProfileActionRow
              to="/profile/mfa"
              label="Multi-factor authentication"
              detail="Manage your authenticator app and verification step."
              badge="Active"
            />
            <ProfileActionRow
              to="/reset-password"
              label="Change password"
              detail="Update the password used to sign in to ASC Survey."
            />
            <ProfileDisabledRow
              label="Notification preferences"
              detail="Preference controls are not available in the field app yet."
            />
            <ProfileDisabledRow
              label="Email signature"
              detail="Signature editing is not available in the field app yet."
            />
            <li>
              <button
                type="button"
                onClick={() => setBugVisible(true)}
                className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[12px] font-bold text-[var(--brand-primary)]"
              >
                <span>🐛 Report a bug</span>
                <span
                  className="rounded px-1.5 py-0.5 text-[9px] font-bold text-slate-500"
                  style={{ background: 'var(--surface-2, #f4f8fd)' }}
                >
                  Alt+B
                </span>
              </button>
            </li>
            <li className="flex items-center justify-between px-3.5 py-2.5 text-[12px] font-bold text-slate-400 opacity-55">
              User management
              <span className="text-[10px]">🔒 admin</span>
            </li>
            <li className="flex items-center justify-between px-3.5 py-2.5 text-[12px] font-bold text-slate-400 opacity-55">
              Billing
              <span className="text-[10px]">🔒 admin</span>
            </li>
            <li className="flex items-center justify-between px-3.5 py-2.5 text-[12px] font-bold text-slate-400 opacity-55">
              Integrations
              <span className="text-[10px]">🔒 admin</span>
            </li>
          </ul>
        </div>

        {/* Sign out */}
        <button
          type="button"
          onClick={() => void signOut()}
          className="inline-flex w-full items-center justify-center rounded-[11px] border border-slate-200 bg-white py-2.5 text-[13px] font-bold text-slate-700 hover:bg-slate-50"
        >
          Sign out
        </button>
      </div>
    </FieldAppShell>
  )
}
